import React, { memo, forwardRef, useRef, useMemo, useImperativeHandle, useContext, useState, useCallback, useEffect } from 'react';
import { Loading } from '../../components/Control';
import { EmpAvatar } from '../../components/EmpUtils';
import { ColGroup } from '../../components/Utils';
import { useBooleanFlag } from '../../components/Hooks';
import { ShiftingContext, ShiftingActionContext, useSidePanelContext } from './ShiftingContext';
import { ShiftingSidePanelTab, ShiftingSidePanel, ShiftSummary, WorkLabelSummary, OverTimeSummary, WorkHourRangeSection, SummarySection } from './ShiftingSidePanelComps';
import { useGridData } from './ShiftingSidePanelHooks';

const { adapt } = LocaleUtils;
const { dateTime } = Utils.Format;
const ISO_FMT = 'YYYY-MM-DD';

const doPersonPreChk = (sessKey, period, empId, setLoading, unsetLoading, setResult, api) => {
	return new _Ajax({
		url: `api/${api}/personPreChk`,
		data: { sessKey, period, empId },
		onRequest: setLoading,
		onSuccess: data => {
			setResult(data);
			unsetLoading();
		},
		onError: xhr => {
			unsetLoading();
		},
		errMsg: '',
		errCode: {},
	}).send();
};


const doPersonSummary = args => {
	const { sessKey, period, empId, empData, setLoading, unsetLoading, setResult, api, keepData } = args;
	const [saved, keepKey, coId] = [!empData.befSummary, keepData?.sessionKey, empData.coId];
	return new _Ajax({
		url: `api/${api}/personSummary`,
		data: { sessKey, period, empId, saved, keepKey, coId },
		onRequest: setLoading,
		onSuccess: data => {
			setResult(data);
			unsetLoading();
		},
		onError: xhr => {
			unsetLoading();
		},
		errMsg: '',
		errCode: {},
	}).send();
};

// 班表檢查結果
export const HourCheckSection = memo(props => {
	const { sessKey, period, empId, saveTmp, empData, setEmpHourResults, skipHour, ...otherProps } = props;
	const [result, setResult] = useState(empData.lastHourChk);
	const [loading, setLoading, unsetLoading] = useBooleanFlag(false);
	const { api } = useContext(ShiftingContext);
	const redoPersonPreChk = useCallback(() => {
		saveTmp({
			onRequest: setLoading,
			onSuccess: () => {
				doPersonPreChk(sessKey, period, empId, setLoading, unsetLoading, data => {
					data.$lastChkTime = new Date();
					empData.lastHourChk = data; // 注意！直接改最上層 cached 的物件

					if (setEmpHourResults) {
						const empResult = { warning: 0, error: 0 };
						_.each(data.result, (value, ruleCode) => {
							_.each(value, (results, checkRule) => {
								if (checkRule === '1') empResult.warning += _.size(results);
								else if (checkRule === '2') empResult.error += _.size(results);
							});
						});
						setEmpHourResults({ [empId]: empResult });
					}

					setResult(data);
				}, api);
			},
			onError: unsetLoading,
		}, [empId], true);
	}, [sessKey, period, empId, saveTmp, empData]);
	return <div {...otherProps}>
		<div className="-desc">
			{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'theResultOfTheWorkRule')}
			<small>
				<span {...{
					className: `clickable${loading ? ' disable' : ''}`,
					...!loading && { onClick: redoPersonPreChk },
				}}><i className={'fa fa-redo-alt'} />
					{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'examine')}
				</span>
			</small>
		</div>
		{loading
			? <Loading size={'2x'} />
			: <>
				<small className={'color-lightGray'}>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'lastCheckTime')} {result ? dateTime(result.$lastChkTime) : '-'}</small>
				{_.isNil(result)
					? <div>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'youHavenTCheckedYourSchedule')}</div>
					: _.isEmpty(result.result)
						? <div>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'complyWithTimeRulesChecks')}</div>
						: (({ ruleBaseMap, result }) => {
							return _.map(_.keys(ruleBaseMap), ruleCode => {
								return <>
									<div key={ruleCode} className={'-errGroup'}>
										<h2 className={'pure'}>{adapt(ruleBaseMap[ruleCode])}</h2>
										{!_.isEmpty(result[ruleCode]['2']) && <>
											<h3 style={{ color: 'rgb(234, 71, 91)' }}
												className={'pure -not-allow fa fa-exclamation-triangle'}>
												{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'forbid')} ({_.size(result[ruleCode]['2'])})
											</h3>
											<ul>
												{_.map(result[ruleCode]['2'], ({ errRng, errMsg }) => <li>{errRng}<br />{errMsg}</li>)}
											</ul>
										</>}
										{!_.isEmpty(result[ruleCode]['1']) && <>
											<h3 className={'pure fa fa-bell'}> {OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'remind')} ({_.size(result[ruleCode]['1'])})</h3>
											<ul>
												{_.map(result[ruleCode]['1'], ({ errRng, errMsg }) => <li>{errRng}<br />{errMsg}</li>)}
											</ul>
										</>}
									</div>
								</>;
							});
						})(result)}
			</>
		}
	</div>;
});
HourCheckSection.displayName = 'HourCheckSection';

const ImmediateSummarySection = ({ gridData }) => {
	const { data } = gridData;

	return (
		<>
			<div className='-desc'>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'manHourStatistics')}</div>
			{
				<>
					<table className={'-shift-side-table'}>
						<ColGroup cols={[120, '*', '*']} />
						<thead>
							<tr>
								<th rowSpan={2}>{OptionUtils.getLabel('hp.m5.m510010.form', 'project')}</th>
								<th colSpan={3}>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'afterStorage')}</th>
								<th rowSpan={2}>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'beforeStorage')}</th>
							</tr>
							<tr>
								<th>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'shouldBeArranged')}</th>
								<th>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'scheduled')}</th>
								<th>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'difference')}</th>
							</tr>
						</thead>
						<tbody>
						{_.map(data, rowData => <tr key={rowData.key}>
							<td>{rowData.name}</td>
							<td>{rowData.should || 0}</td>
							<td>{rowData.aft || 0}</td>
							<td>{rowData.shouldDiff || 0}</td>
							<td>{rowData.bef || 0}</td>
						</tr>)}
						</tbody>
					</table>
				</>
			}
		</>
	);
};

// 即時班表統計
const Summary = memo(forwardRef((props, ref) => {
	const { sessKey, period, empId, saveTmp, empData, openStatisticsDlg, keepData, skipHour } = props;
	const { befSummary } = empData;
	const { shifts, workLabels, calendarLeaves, api } = useContext(ShiftingContext);
	const { openLeaveDetailDlg } = useContext(ShiftingActionContext);
	const { summaryOptions } = useSidePanelContext();
	const ajaxRef = useRef(null);
	const [aftSummary, setAftSummary] = useState(null);
	const [loading, setLoading, unsetLoading] = useBooleanFlag(true);

	const redoPersonSummary = useCallback(() => {
		const setResult = data => {
			const [tmpResult, savedResult] = data;
			if (savedResult) empData.befSummary = savedResult; // 注意！直接改最上層 cached 的物件
			setAftSummary(tmpResult);
		};
		const saveRet = saveTmp({
			onRequest: setLoading,
			onSuccess: () => {
				ajaxRef.current = doPersonSummary({
					sessKey,
					period,
					empId,
					empData,
					setLoading,
					unsetLoading,
					setResult,
					api,
					keepData,
				});
			},
			onError: unsetLoading,
		}, [empId], true);
		if (!_.isString(saveRet))
			ajaxRef.current = saveRet;
	}, [sessKey, period, empId, saveTmp, befSummary, empData]);

	const gridData = useGridData(aftSummary, befSummary, calendarLeaves);

	useImperativeHandle(ref, () => ({
		redoPersonSummary,
	}), [redoPersonSummary]);

	useEffect(() => {
		redoPersonSummary();
		return () => {};
	}, []);

	if (loading) return <Loading size={'2x'} />;

	// if (skipHour) {
	// 	return <ImmediateSummarySection gridData={gridData} />;
	// }

	const SummaryComps = {
		summarySection: <SummarySection data={aftSummary} calendarLeaves={calendarLeaves} />,
		immediateSummarySection: <ImmediateSummarySection gridData={gridData} />,
		workHourRangeSection: <WorkHourRangeSection workHourRangeJson={aftSummary.workHourRangeJson} />,
		shiftSummary: <ShiftSummary shiftJson={aftSummary.shiftJson} shifts={shifts} shiftJsonWeekday={aftSummary.shiftJsonWeekday} />,
		workLabelSummary: <WorkLabelSummary workLabelJson={aftSummary.workLabelJson} workLabels={workLabels} workLabelJsonWeekday={aftSummary.workLabelJsonWeekday}/>,
		overTimeSummary: <OverTimeSummary
			{...aftSummary}
			link={
				openLeaveDetailDlg &&
					<span className={'link'} onClick={() => {
						openLeaveDetailDlg({ empId, period });
					}}>{OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'leaveAndOvertimeDetails')}</span>
			}
		/>
	};

	return <>
		{
			summaryOptions.sections?.map(compKey => {
				return <React.Fragment key={compKey}>
					{SummaryComps[compKey] || null}
				</React.Fragment>;
			})
		}
		{/* {!empData.preMode && <div className="text-right">
			<span className={'clickable'} onClick={openStatisticsDlg}>查看完整人力統計表</span>
		</div>} */}
	</>;
}));

export const SidePanel = memo(forwardRef((props, ref) => {
	const {
		sessKey, dates, sideData: empData, close, setEmpHourResults, saveTmp, openStatisticsDlg,
		skipHour, keepData, ...otherProps
	} = props;
	const { emp, $edit: edit } = empData;
	const empId = emp.id;
	const summarySectRef = useRef();
	const { summaryOptions } = useSidePanelContext();
	const period = useMemo(() => {
		return [dates[0].format(ISO_FMT), dates[dates.length - 1].format(ISO_FMT)];
	}, [dates]);
	useImperativeHandle(ref, () => ({
		updateSidePanel: () => {
			if (summarySectRef.current)
				summarySectRef.current.redoPersonSummary();
		},
	}), []);
	return <ShiftingSidePanel {...otherProps}>
		<div {...{ className: '-close fal fa-times', onClick: close }} />
		<div className={'-nameCard'}>
			<EmpAvatar empId={empId} size={60} className={'-head'} />
			<div className={'-name'}>
				{emp.name || emp.enName}
				<span className={'-empNo'}>{emp.empNo}</span>
				{!edit && <><br />
					<div className="-non-edit">*{OptionUtils.getLabel('h2.am.Shifting', 'noEditingPermissions')}</div>
				</>}
			</div>
		</div>
		<hr className={'tight'} />
		<ShiftingSidePanelTab name={summaryOptions.name}>
			<Summary {...{
				key: `statistic_${empId}`,
				ref: summarySectRef,
				sessKey, period, saveTmp,
				empId, empData,
				openStatisticsDlg,
				keepData,
				skipHour
			}} />
		</ShiftingSidePanelTab>
		{
			!skipHour ? (
				<ShiftingSidePanelTab name={OptionUtils.getLabel('h2.am.ShiftingSidePanel', 'scheduleCheckResults')}>
					<HourCheckSection {...{
						key: `hourCheckSection_${empId}`,
						sessKey, period, saveTmp,
						empId, empData,
						setEmpHourResults,
						skipHour
					}} />
				</ShiftingSidePanelTab>
			) : null
		}

	</ShiftingSidePanel>;
}));
SidePanel.displayName = 'SidePanel';
